import {
  type OpportunitiesSearchFilters,
  type OpportunitiesSearchOptions,
  type OpportunitySearch,
  useMultiupdateOpportunity,
} from "@repo/api";
import { InlineMessage, Text } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  type SelectListItem,
  SelectListView,
  useOrderedCustomerMeetingPoints,
  useToast,
} from "@repo/ui";
import { getErrorMessage } from "@repo/utils";
import { useState } from "react";

import { type EditModalMode } from "../utils";
import { MultiUpdateModal, createSaveSucess } from "./MultiUpdateModal";

interface EditOpportunitiesModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<EditModalMode>>;
  selectedOpportunities: OpportunitySearch[];
  setSelectedOpportunities: React.Dispatch<
    React.SetStateAction<OpportunitySearch[]>
  >;
  options: OpportunitiesSearchOptions;
  filters: OpportunitiesSearchFilters;
}
export const EditOpportunitiesModal = ({
  isOpen,
  setIsOpen,
  selectedOpportunities,
  setSelectedOpportunities,
  options,
  filters,
}: EditOpportunitiesModalProps) => {
  const { updateToast } = useToast();
  const { data: customerMeetingPoints } = useOrderedCustomerMeetingPoints();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [selectedBuCode, setSelectedBuCode] = useState<string | undefined>(
    undefined
  );

  const onChange = (_: boolean, item: SelectListItem) => {
    setSelectedBuCode(item.value);
  };

  const updateProjects = useMultiupdateOpportunity(filters, options);

  const onSave = (selectedProjects: OpportunitySearch[]) => {
    setIsLoading(true);
    updateProjects(
      { customerMeetingPointBuCode: selectedBuCode },
      selectedProjects.map((el) => el.opportunityId)
    )
      .then(
        createSaveSucess({
          setSelectedOpportunities,
          selectedOpportunities,
          selectedProjects,
          updateToast,
          setIsOpen,
        })
      )
      .catch((e) => {
        updateToast({
          id: selectedProjects.map((el) => el.opportunityId).join(","),
          title: t("prompt.project_saved_failed"),
          message: getErrorMessage(e),
          type: "error",
        });
      })
      .finally(() => setIsLoading(false));
  };

  const mappedValueList =
    customerMeetingPoints?.map((el) => ({
      value: el[0],
      name: el[0],
      description: el[1],
    })) ?? [];

  return (
    <MultiUpdateModal
      title={t("action.change_cmp")}
      isOpen={isOpen}
      isLoading={isLoading}
      setIsOpen={setIsOpen}
      selectedOpportunities={selectedOpportunities}
      onSave={selectedBuCode ? onSave : undefined}
    >
      <Text textStyle="Heading.S">{t("action.change_cmp")}</Text>
      <InlineMessage
        title={t("label.assigned_coworker")}
        body={t("prompt.unassigned_after_changing_cmp")}
        variant="informative"
      />

      <SelectListView
        id="cmp-list"
        list={mappedValueList}
        selectedKeys={selectedBuCode ? [selectedBuCode] : []}
        onChange={onChange}
        selectionMode="SINGLE"
      />
    </MultiUpdateModal>
  );
};
